<template>
  <div
    v-if="paymentInfo.businessEntity && paymentInfo.employees.length"
    v-loading.fullscreen.lock="fullScreenLoading"
    class="payment-page"
  >
    <div
      class="header"
      :style="{ background: schemaPrimaryColor }"
    >
      <el-row
        type="flex"
        class="header-row"
      >
        <div class="header-logo">
          <el-image
            class="header-logo-img"
            :src="profileImage"
            :preview-src-list="[profileImage]"
          />
        </div>
        <div>
          <h3 class="header-title">
            {{ paymentInfo.businessEntity.name }}
          </h3>
        </div>
      </el-row>
    </div>
    <div class="body">
      <el-collapse
        class="body-amount-collapse"
        v-model="activeCollapse"
      >
        <el-collapse-item
          class="primary-outline"
          v-for="(employee, index) in paymentInfo.employees"
          :name="employee.qrCode"
          :key="index"
          :style="{ '--employee-color': schemaPrimaryColor }"
        >
          <defined-tips-amount
            :elements="paymentInfo.team.defTipsAmount"
            :model="employee.amount"
            :currency="paymentInfo.currency"
            :primary-color="schemaPrimaryColor"
            :presentation-mode="paymentInfo.businessEntity.presentationMode"
            :employee="employee"
            @change="changeDefinedTips"
          />
          <template slot="title">
            <img
              :src="
                `https://tippiie-images.s3.eu-central-1.amazonaws.com/public/${employee.photoUrl ||
                  `${employee.empUid}.png`}?t=${new Date().getTime()}`
              "
              :alt="employee.name"
              width="40px"
              height="40px"
            >
            <div class="title">
              <p class="body-amount-collapse-title">
                {{
                  employee.nickName
                    ? employee.nickName
                    : `${employee.name} ${employee.familyName}`
                }}
              </p>
            </div>
          </template>
          <p class="body-amount-collapse-label">
            {{ $t("pay.input-amount-text") }}
          </p>
          <currency-input
            :presentation-mode="
              paymentInfo &&
                paymentInfo.businessEntity &&
                paymentInfo.businessEntity.presentationMode
            "
            v-model="employee.amount"
            :options="currencyInputOptions"
            @input="inputAmount"
          />
        </el-collapse-item>
      </el-collapse>
      <el-row
        v-if="
          paymentInfo.businessEntity.feePercentage > 0 &&
            totalPay > 0 &&
            !paymentInfo.businessEntity.waiterPaysFee &&
            allPopulated
        "
      >
        <fee-checkbox
          v-model="payFee"
          :strategy="payGuest"
          :currency="paymentInfo.currency"
          :total-amount="amount"
          :fee-percentage="paymentInfo.businessEntity.feePercentage"
          :fee-amount.sync="paymentInfo.feeAmount"
          :payment-for="paymentFor"
          :primary-color="schemaPrimaryColor"
        />
      </el-row>
      <el-row
        v-if="totalPay >= minAmount && allPopulated"
        class="body-total-amount"
      >
        <span>
          {{ $t("pay.total-amount") }}:
          {{
            (totalPay / 100).toLocaleString($localeString, {
              style: "currency",
              currency: paymentInfo.currency,
            })
          }}
        </span>
      </el-row>
      <el-row class="body-payment-methods">
        <h2
          v-if="!allPopulated"
          class="body-payment-methods-small-amount"
        >
          {{ $t("form.validation.all-items-populated") }}
        </h2>
        <PaymentMethods
          v-else-if="totalPay >= minAmount"
          :client-pays-fee="payFee"
          :terms-agree="termsAgree"
          :total-amount="totalPay"
          :tip-amount="amount"
          :currency="paymentInfo.currency"
          :qr-codes="paymentInfo.qrCodes"
          :business="paymentInfo.businessEntity"
          :payment-object="paymentObject"
          :primary-color="schemaPrimaryColor"
          :is-multiple="true"
        />
        <h2
          v-else
          class="body-payment-methods-small-amount"
        >
          {{ $t("pay.small-amount") }}
        </h2>
      </el-row>
      <el-row class="body-terms">
        <el-checkbox v-model="termsAgree">
          <i18n path="pay.user-terms">
            <template v-slot:termsLink>
              <a
                class="terms-link"
                href="https://tippie.de/agb/"
                target="_blank"
              >
                {{ $t("pay.user-terms-link") }}
              </a>
            </template>
          </i18n>
        </el-checkbox>
      </el-row>
      <el-row
        type="flex"
        justify="center"
        class="body-certification-logo"
      >
        <img
          src="@/assets/img/cert/SMiG_RGB_150dpi.png"
          alt="SMiG"
        >
        <img
          class="ml-25"
          src="@/assets/img/cert/logo_SHiG.png"
          alt="SHiG"
        >
      </el-row>
      <el-row class="body-logo">
        <el-link
          class="body-logo-image-link"
          :underline="false"
          href="https://tippie.de"
          target="_blank"
        >
          <img
            src="@/assets/img/logo.png"
            alt="Tippie"
          >
        </el-link>
        <el-link
          :underline="false"
          class="body-logo-text-link"
          href="https://tippie.de"
          target="_blank"
        >
          {{ $t("visit-us") }}
        </el-link>
      </el-row>
    </div>
  </div>
</template>

<script>
import DefinedTipsAmount from "./Components/DefinedTipsAmount";
import CurrencyInput from "../Components/CurrencyInput";
import PaymentMethods from "./PaymentMethods/index";
import FeeCheckbox from "./Components/FeeCheckbox";
import PayGuestStrategy from "../../shared/PayGuestStrategy";

export default {
  name: "MultiplePay",
  components: {
    FeeCheckbox,
    DefinedTipsAmount,
    CurrencyInput,
    PaymentMethods,
  },
  setup() {
    const payGuest = new PayGuestStrategy();

    return {
      payGuest,
    };
  },
  data: function() {
    return {
      imageURLPath:
        "https://tippiie-images.s3.eu-central-1.amazonaws.com/public/",
      videoPlayed: false,
      fullScreenLoading: true,
      paymentInfo: {
        currency: this.$route.params.currency,
        amount: this.$route.params.amount || 0,
        qrCodes: this.$route.params.qrCodes
          ? this.$route.params.qrCodes.split(",").map(Number)
          : [],
        employees: [],
        businessEntity: {},
        feeAmount: 0,
      },
      currencyInputOptions: {
        currency: this.$route.params.currency,
        locale: this.$i18n.locale,
        precision: 2,
        valueRange: {
          min: 0,
          max: 1000,
        },
        valueScaling: "precision",
      },
      termsAgree: true,
      showPane: false,
      timeout: null,
      payFee: true,
      minAmount: 50,
      activeCollapse: [],
    };
  },
  async created() {
    if (
      process.env.VUE_APP_I18N_SUPPORTED_LOCALE.split(",").includes(
        this.$route.params.locale
      )
    ) {
      this.$i18n.locale = this.$route.params.locale;
    }
    await this.$axios
      .post(`api/v1/qrcodes`, {
        qrCodes: this.paymentInfo.qrCodes,
      })
      .then((response) => {
        if (response.data.validness) {
          this.paymentInfo.employees =
            response.data.employees && response.data.employees.length
              ? response.data.employees
              : [];
          this.paymentInfo.employees.forEach((el) => {
            el.amount = 0;
            this.activeCollapse.push(el.qrCode);
          });
          this.paymentInfo.businessEntity = response.data.org || {};

          this.checkClientPaysFee();

          this.paymentInfo.team = response.data.team || {};
        } else {
          this.$message.error("Provided QR Codes is not valid");
        }
        this.fullScreenLoading = false;
      })
      .catch((error) => {
        console.log(error);
        this.fullScreenLoading = false;
      });
  },
  mounted() {
    this.$amplitude.logEvent("Payment page event Page loaded", {
      qrCode: this.paymentInfo.qrCodes.join(","),
    });
    this.$axios.post("/api/v1/fe-status", {
      status: 200,
      message: "Payment page event Page loaded",
      qrCode: this.paymentInfo.qrCodes.join(","),
      timestamp: Date.now(),
    });
  },
  computed: {
    profileImage() {
      return `${this.imageURLPath}${this.paymentInfo?.businessEntity?.logo ||
        "profile.png"}?t=${new Date().getTime()}`;
    },
    paymentFor() {
      // if(this.paymentInfo.team.showsTeamName) {
      //   return this.paymentInfo.team.name
      // }

      return this.paymentInfo.businessEntity.name;
    },
    schemaPrimaryColor() {
      return this.paymentInfo.businessEntity.schemaPrimaryColor || "#FF8A00";
    },
    totalPay: function() {
      let total = this.amount;

      if (this.payFee) {
        this.payGuest.setAmount(total);
        return this.payGuest.calculateTotal();
      }
      return total;
    },
    amount: function() {
      let total = 0;
      this.paymentInfo.employees.forEach((el) => {
        total += el.amount;
      });

      return total;
    },
    paymentObject: function() {
      return this.paymentInfo.employees.map((el) => {
        return {
          qrCode: el.qrCode,
          amount: el.amount,
          currency: this.$route.params.currency,
        };
      });
    },
    allPopulated: function() {
      let populated = true;
      this.paymentInfo.employees.forEach((el) => {
        if (!el.amount || el.amount <= 0) {
          populated = false;
        }
      });
      return populated;
    },
  },
  methods: {
    checkClientPaysFee() {
      if (this.paymentInfo.businessEntity.clientPaysFeePricing == null) {
        this.payGuest.setClientPaysFeeNull(true);
        this.payGuest.setFeePercentage(
          this.paymentInfo.businessEntity.feePercentage
        );
      } else {
        const orgFeeParams = this.paymentInfo.businessEntity
          .clientPaysFeePricing;
        const percentageFeeAboveThreshold =
          orgFeeParams.percentageFeeAboveThreshold > 0
            ? orgFeeParams.percentageFeeAboveThreshold
            : this.paymentInfo.businessEntity.feePercentage / 100;
        this.payGuest.setClientPaysFeeNull(false);
        this.payGuest.setFixedFee(orgFeeParams.fixedFee);
        this.payGuest.setThreshold(orgFeeParams.threshold);
        this.payGuest.setPercentageFeeAboveThreshold(
          percentageFeeAboveThreshold
        );
      }
    },
    finishVideo() {
      this.videoPlayed = true;
    },
    inputAmount(value) {
      if (this.timeout !== null) {
        clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => {
        this.$amplitude.logEvent("Payment page event Amount change", {
          qrCode: this.paymentInfo.qrCodes.join(","),
          amount: value,
        });
        this.$axios.post("/api/v1/fe-status", {
          status: 200,
          message: "Payment page event Amount change",
          data: value,
          qrCode: this.paymentInfo.qrCodes.join(","),
          timestamp: Date.now(),
        });
      }, 1000);
      if (this.payFee) {
        this.payGuest.setAmount(value);
        this.paymentInfo.feeAmount = this.payGuest.calculateFee();
      }
      this.paymentInfo.employees.splice();
      this.$forceUpdate();
    },
    changeDefinedTips(value, qrCode = null) {
      const foundEmployee = this.paymentInfo.employees.find(
        (employee) => employee.qrCode === qrCode
      );
      if (foundEmployee) {
        this.$set(foundEmployee, "amount", value);
        this.paymentInfo.employees.splice();
        this.$forceUpdate();
      }
    },
  },
};
</script>
<style lang="scss">
.body-amount-collapse {
  &.el-collapse .el-collapse-item.primary-outline .el-collapse-item__arrow {
    color: var(--employee-color);
  }

  &.el-collapse .el-collapse-item.primary-outline {
    border-color: var(--employee-color);
  }

  &.el-collapse
    .el-collapse-item.primary-outline
    .el-collapse-item__header.is-active {
    background: var(--employee-color);
    .title {
      color: #fff;
    }
  }
  .currency-input-clear {
    .el-icon-close::before {
      color: var(--employee-color);
    }
  }
}
</style>
<style scoped lang="scss">
.header {
  background: #ff8a00;
  padding: 24px 12px;

  &-row {
    align-items: center;
  }

  &-logo {
    margin-right: 30px;

    &-img {
      width: 85px;
      height: 85px;
      border-radius: 50%;
      border: 2px solid #ffffff;
    }
  }

  &-title {
    margin: 0;
    font-family: "Raleway Bold", serif;
    color: #ffffff;
    font-size: 28px;
    font-weight: 900;
    word-break: break-word;
  }
}

.body {
  padding: 0px 12px 30px;

  &-payment {
    &-methods {
      &-small-amount {
        margin-top: 24px;
        text-align: center;
        color: #2d2e2f;
        font-family: "Raleway Bold", serif;
        font-size: 16px;
      }
    }
  }

  &-total-amount {
    text-align: center;
    margin-top: 25px;
    margin-bottom: 12px;
    font-family: "Raleway Bold", serif;
    font-feature-settings: "pnum" on, "lnum" on;
    font-size: 16px;
    color: #2d2e2f;
  }

  &-amount {
    &-collapse {
      width: 100%;
      max-width: 730px;
      margin: 50px auto 0 auto;

      &-title {
        margin-left: 20px;
      }

      &-label {
        font-family: "Raleway Regular", serif;
        font-size: 16px;
        font-feature-settings: "tnum" on, "lnum" on;
      }
    }

    &-text {
      font-family: "Raleway Bold", serif;
      font-feature-settings: "pnum" on, "lnum" on;
      font-size: 16px;
      color: #2d2e2f;
      text-align: center;
      margin-top: 12px;
    }
  }

  &-terms::v-deep {
    max-width: 350px;
    margin: 25px auto 0 auto;

    .el-checkbox {
      display: flex;
      align-items: flex-start;
      width: 100%;

      &__inner {
        width: 16px;
        height: 16px;
        background: #2d2e2f;
        border: none;
        border-radius: 2px;

        &:after {
          height: 8px;
          width: 4px;
          border: 2px solid #ffffff;
          border-left: 0;
          border-top: 0;
          left: 5px;
          top: 2px;
        }
      }

      &__label {
        font-family: "Raleway Medium", serif;
        font-size: 12px;
        line-height: 16px;
        color: #2d2e2f;

        span {
          white-space: break-spaces;
        }

        .terms-link {
          color: #b5b5b5;
          text-decoration: underline;
        }
      }
    }
  }

  &-logo {
    text-align: center;
    display: flex;
    flex-direction: column;
    margin-top: 16px;

    img {
      width: 100px;
      height: 60px;
    }

    a {
      width: max-content;
      margin: 0 auto;

      &.body-logo-text-link {
        margin-top: 10px;
      }
    }
  }

  &-certification-logo {
    margin-top: 10px;

    .el-col {
      @media (max-width: 768px) {
        text-align: center;
        &:nth-child(2n) {
          margin-top: 10px;
        }
      }
    }

    img {
      height: 80px;
    }
  }
}
</style>
